/**
 * "What next" — the one sensible thing to do when no job is running, worked out from the mission
 * service's job board (any mission list: story jobs, export orders, street jobs), the law, the wallet and
 * a waypoint the player marked in the book. Pure; unit-tested in onboarding.test.ts.
 */
import type { JobBoardEntry } from '../missions/missionSystem';
import { ladderStep, type LadderFacts } from './onlineLadder';

export interface Waypoint {
  x: number;
  z: number;
  label: string;
  /** Where it was marked: the book (default) or a tap on the full map. */
  from?: 'book' | 'map';
}

export interface NextStep {
  kind: 'law' | 'waypoint' | 'story' | 'export' | 'street' | 'activity' | 'online' | 'goal' | 'free';
  /** Clipping headline. */
  title: string;
  text: string;
  hint?: string;
  target?: Waypoint;
  /** The job this step leads to (story / export / street). */
  jobId?: string;
}

export interface NextInput {
  /** A job is running: its own objective is on screen, the tracker stays out of the way. */
  missionActive: boolean;
  heat: number;
  waypoint: Waypoint | null;
  online: boolean;
  jobs: readonly JobBoardEntry[];
  money: number;
  lockupOwned: boolean;
  lockupPrice: number;
  lockup: Waypoint | null;
  player: { x: number; z: number };
  /** Online: places with a job board (phone boxes, pubs, Halloran's). */
  boards?: readonly Waypoint[];
  /** Online: what the city's books say about you (from the online store). */
  street?: OnlineFacts;
  /**
   * Offline, the contact ladders' next rung for the repeat work (WP-X1, shared/contacts.ts `horizonLine`): Halloran's
   * for an export order, Lenny's for a street job. Added to that step's hint once the story has nothing waiting.
   */
  horizon?: { export?: string; street?: string };
}

/** Online facts for the tracker, read from the online store. All optional: an empty store means "find work". */
export interface OnlineFacts {
  cash: number;
  /** A courier / recovery job or a Night Freight run is on: its own objective is on screen. */
  busy: boolean;
  /** An invite waiting for an answer. */
  invite: { kind: 'gang' | 'crew'; from: string } | null;
  /** Crew members in the city now, you included (0 = no crew). */
  crewOnline: number;
  inGang: boolean;
  /** Touch controls: no Y / N keys (the top of the book answers invites). */
  touch?: boolean;
  /** Bonded Warehouse No. 3 (the Night Freight pier) and the bank, for the marker. */
  pier?: Waypoint;
  bank?: Waypoint;
  /** The first-evening ladder (onlineLadder.ts); absent = a character past it (only the live checks run). */
  ladder?: LadderFacts;
  /** Past the ladder: the nearest contact rung (WP-X1), the "Find work" hint. */
  horizon?: string;
}

/** Cash on hand above this is worth banking (a fine takes ~12 % of it, a mugger the lot). */
export const BANK_NUDGE = 1_500;

/** "The Crooked Anchor (back room)" → "The Crooked Anchor". */
export function placeName(name: string): string {
  return name.replace(/\s*\([^)]*\)\s*$/, '');
}

/** "Mags Delaney" → "Mags"; "Mr Pargeter" → "Mr Pargeter". */
export function firstName(client: string): string {
  const parts = client.trim().split(/\s+/);
  return /^(Mr|Mrs|Miss|Ms|DS|DI|Dr)\.?$/.test(parts[0] ?? '') ? client.trim() : parts[0] ?? client;
}

const pounds = (n: number): string => `£${Math.round(n).toLocaleString('en-GB')}`;

function jobStep(j: JobBoardEntry): NextStep {
  const where = placeName(j.giver.name);
  const target = { x: j.giver.x, z: j.giver.z, label: `${firstName(j.client)} — ${where}` };
  const hint = `${j.giver.prompt} — press E there. “${j.title}” pays ${pounds(j.reward)}.`;
  if (j.kind === 'export') {
    return {
      kind: 'export',
      title: 'Export order',
      text: `${j.client} pays for stolen cars to ship abroad. See him at ${where}.`,
      hint: `${j.giver.prompt} — press E there.`,
      target,
      jobId: j.id,
    };
  }
  if (j.kind === 'activity') {
    // Something new to try between jobs (a street race, a minicab shift): say what it is, not "a job".
    return {
      kind: 'activity',
      title: j.kicker ? `New: ${j.kicker.split(' · ')[0]}` : j.title,
      text: j.giver.phone ? `${j.client} has work. Call from ${where}.` : `${firstName(j.client)} has something on at ${where}. Go and see.`,
      hint: `${j.giver.prompt} — press E there.`,
      target,
      jobId: j.id,
    };
  }
  if (j.giver.phone) {
    return { kind: j.kind === 'street' ? 'street' : 'story', title: j.title, text: `A phone is ringing at ${where}. Go and answer it.`, hint, target, jobId: j.id };
  }
  return { kind: j.kind === 'street' ? 'street' : 'story', title: j.title, text: `Go and see ${firstName(j.client)} at ${where}.`, hint, target, jobId: j.id };
}

/** The closest of `places` to `from` (undefined when there are none). */
export function nearest(places: readonly Waypoint[], from: { x: number; z: number }): Waypoint | undefined {
  let best: Waypoint | undefined;
  let bd = Infinity;
  for (const b of places) {
    const d = Math.hypot(b.x - from.x, b.z - from.z);
    if (d < bd) {
      bd = d;
      best = b;
    }
  }
  return best;
}

export function nextStep(i: NextInput): NextStep | null {
  if (i.missionActive) return null;
  if (i.heat > 0) {
    return {
      kind: 'law',
      title: 'Lose the police',
      text: 'Get out of their sight — round corners, down alleys — and put distance between you.',
      hint:
        i.heat >= 2
          ? i.online
            ? 'Your wanted level drops once they lose you. Pike’s Respray (the spray gun on the map) repaints a car the police know.'
            : 'Your wanted level drops once they lose you. Pike’s Respray (the spray gun on the map) repaints a car the police know — or, out of their sight, ring the desk sergeant from a phone box.'
          : 'Your wanted level drops once they lose you.',
    };
  }
  if (i.waypoint) {
    return {
      kind: 'waypoint',
      title: 'Marked',
      text: `Head for ${i.waypoint.label}.`,
      hint:
        i.waypoint.from === 'map'
          ? 'Follow the arrows on the road. The route clears when you get there; to clear it sooner, tap your flag on the map.'
          : 'Follow the arrows on the road. The route clears when you get there.',
      target: i.waypoint,
    };
  }
  if (i.online) {
    const st = i.street;
    if (st?.busy) return null;
    if (st?.invite) {
      return {
        kind: 'online',
        title: st.invite.kind === 'gang' ? 'A gang wants you' : 'Crew invite',
        text: `${st.invite.from} wants you in their ${st.invite.kind}. ${st.touch ? 'Open your book: ACCEPT / DECLINE are at the top.' : 'Answer with Y or N, or in your book.'}`,
        hint: st.invite.kind === 'gang' ? 'A gang shares a treasury, premises and turf. You can leave whenever you like.' : 'A crew is for tonight: chat, your mates on the map, Night Freight.',
      };
    }
    if (st && st.crewOnline >= 2) {
      return {
        kind: 'online',
        title: 'Night Freight',
        text: 'Your crew is about. Meet at Bonded Warehouse No. 3, one of you driving a car with a boot, and press E at the pier.',
        hint: `Crates cost £300 and fetch £650 at the drop.${st.inGang ? ' Two or more of your gang on the run earns the gang reputation.' : ''}`,
        ...(st.pier ? { target: st.pier } : {}),
      };
    }
    if (st && st.cash >= BANK_NUDGE) {
      return {
        kind: 'online',
        title: 'Bank it',
        text: `You’re carrying ${pounds(st.cash)}. Pay it in at the bank or a cashpoint — press E at the door.`,
        hint: 'A fine or a hospital bill comes out of your pocket, never the bank.',
        ...(st.bank ? { target: st.bank } : {}),
      };
    }
    // The first evening (onlineLadder.ts): loaner → a job → the bank → Sid's → a crew → Night Freight → a gang → premises.
    if (st?.ladder) {
      const rung = ladderStep(st.ladder, nearest(i.boards ?? [], i.player), !!st.touch);
      if (rung) return rung;
    }
    let best: Waypoint | undefined;
    let bd = Infinity;
    for (const b of i.boards ?? []) {
      const d = Math.hypot(b.x - i.player.x, b.z - i.player.z);
      if (d < bd) {
        bd = d;
        best = b;
      }
    }
    return {
      kind: 'online',
      title: 'Find work',
      // "press E" in the form the touch rewrite knows (the HUD rewords the text for the knobs as it does the hint).
      text: best ? `Nearest job board: ${best.label} — press E at the door.` : 'Job boards are at phone boxes, pubs and Halloran’s — press E at the door.',
      hint:
        st?.horizon ??
        (st && st.crewOnline === 0
          ? `Mates online? Crew up (${st.touch ? 'book → Crew' : 'C'}) — Night Freight from the pier pays best, and only a crew can run it.`
          : 'Your book (press I) has the jobs, your gang and your crew.'),
      ...(best ? { target: best } : {}),
    };
  }
  const story = i.jobs.find((j) => j.kind === 'story' && j.available);
  if (story) return jobStep(story);
  // A side activity that's open but never tried yet (src/activities): worth a mention once.
  const fresh = i.jobs.find((j) => j.kind === 'activity' && j.available && !j.completed);
  if (fresh) return jobStep(fresh);
  // The lockup as soon as it's affordable: Halloran's order is always open, so behind it the goal was never named.
  if (!i.lockupOwned && i.lockup && i.money >= i.lockupPrice) {
    return { kind: 'goal', title: 'Treat yourself', text: `You can afford the lockup key: ${i.lockup.label}.`, hint: 'Go and press E at the lockup door.', target: i.lockup };
  }
  const exp = i.jobs.find((j) => j.kind === 'export' && j.available);
  const ahead = (s: NextStep, h: string | undefined): NextStep => (h ? { ...s, hint: s.hint ? `${s.hint} ${h}` : h } : s);
  if (exp) return ahead(jobStep(exp), i.horizon?.export);
  const street = i.jobs.find((j) => j.kind === 'street' && j.available);
  if (street) return ahead(jobStep(street), i.horizon?.street);
  if (!i.lockupOwned && i.money < i.lockupPrice) {
    return {
      kind: 'goal',
      title: 'Save up',
      text: `Put ${pounds(i.lockupPrice - i.money)} more together for the lockup key — a car of your own, replaced if it’s wrecked.`,
      hint: 'Halloran’s export orders and the street jobs pay well.',
      ...(i.lockup ? { target: i.lockup } : {}),
    };
  }
  if (!i.lockupOwned && i.lockup) {
    return { kind: 'goal', title: 'Treat yourself', text: `You can afford the lockup key: ${i.lockup.label}.`, hint: 'Go and press E at the lockup door.', target: i.lockup };
  }
  return { kind: 'free', title: 'Free roam', text: 'The borough’s yours. Steal something quick and see how long you can keep the police busy.' };
}
